import { Component, Input, ViewChild, OnDestroy, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BackOffice24StatesService } from '../../state/back-office24-states.service';
import { catchError, of, Subscription, finalize, takeUntil, Subject } from 'rxjs';
import { MyConfigOrganization } from '../../../../../models/myConfig';
import { DebtOrder, OrderPaginationModel } from '../../../../../models/orderModel';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';

@Component({
  selector: 'app-debt-list',
  standalone: false,
  templateUrl: './debt-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './debt-list.component.css'
})
export class DebtListComponent implements OnInit, OnDestroy {
  private orgSubscription: Subscription = new Subscription();
  private destroy$ = new Subject<void>();

  @Input() organization: MyConfigOrganization;
  @Input() isLoading: boolean = false;
  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();

  displayedColumns: string[] = [
    'orderNr',
    'customerName',
    'cellular',
    'carId',
    'debtAmount',
    'additionalPaymentSent',
    'debtReminder1Sent',
    'debtReminder2Sent',
    'debtReminder3Sent',
    'invoice'
  ];

  // Map frontend column names to backend column names
  private readonly columnMapping: { [key: string]: string } = {
    'orderNr': 'OrderNr',
    'customerName': 'Name',
    'cellular': 'Phone',
    'carId': 'RegNo'
  };

  // last sent debt notification, matches DebtStage enum on backend
  stageOptions: { value: number | null, translation: string }[] = [
    { value: null, translation: 'ORDER_LIST.DEBTS.STAGE_ALL' },
    { value: 0, translation: 'ORDER_LIST.DEBTS.STAGE_NONE' },
    { value: 1, translation: 'ORDER_LIST.DEBTS.STAGE_PAYMENT_REQUEST' },
    { value: 2, translation: 'ORDER_LIST.DEBTS.STAGE_1' },
    { value: 3, translation: 'ORDER_LIST.DEBTS.STAGE_2' },
    { value: 4, translation: 'ORDER_LIST.DEBTS.STAGE_3' }
  ];
  selectedStage: number | null = null;

  orders: DebtOrder[] = [];
  dataSource: MatTableDataSource<DebtOrder> = new MatTableDataSource<DebtOrder>([]);
  totalData: number = 0;
  pageSizes: number[] = [25, 50, 100];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    public backofficeService: BackOfficeService,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    public router: Router,
    public backOffice24StatesService: BackOffice24StatesService,
  ) { }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(
      org => this.organization = org
    );

    this.paginator.page.pipe(takeUntil(this.destroy$)).subscribe(() => this.loadData());
    this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.paginator.pageIndex = 0;
      this.loadData();
    });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.orgSubscription.unsubscribe();
  }

  loadData() {
    if (!this.organization) return;

    const sortActive = this.sort?.active || 'orderNr';
    const sortDirection = this.sort?.direction || 'desc';

    const filter: OrderPaginationModel = {
      SortingColumn: this.columnMapping[sortActive] || 'OrderNr',
      SortingDirection: sortDirection,
      Paginate: {
        PerPage: this.paginator?.pageSize || this.pageSizes[0],
        Page: this.paginator ? this.paginator.pageIndex + 1 : 1
      }
    };
    if (this.selectedStage !== null) {
      filter.DebtStage = this.selectedStage;
    }

    this.incrementRequests.emit();

    this.backofficeService.getPaginatedDebts(this.organization.organizationId, filter).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading debts:', error);
        return of({ data: [], pagination: { total: 0 } });
      }),
      finalize(() => this.decrementRequests.emit())
    ).subscribe((response: any) => {
      this.orders = response?.data || [];
      this.totalData = response?.pagination?.total || 0;

      this.orders.forEach(order => {
        if (order.customer) {
          order.customerName = `${order.customer.firstName} ${order.customer.lastName}`;
          order.cellular = order.customer.cellular;
        }
      });

      this.dataSource.data = this.orders;
    });
  }

  onStageChange() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  createInvoice(order: DebtOrder, event: Event) {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill gör invoice för order ${order.orderNr}?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.incrementRequests.emit();
      this.backofficeService
        .createInvoice(order.orderId)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.decrementRequests.emit())
        )
        .subscribe({
          next: () => {
            this.snackBar.open(`Du har görde invoice`, 'OK', {
              duration: 2000,
              verticalPosition: 'top'
            });
            this.loadData();
          },
          error: () => {
            this.snackBar.open(`Fel vid skapande av invoice`, 'OK', {
              duration: 2000,
              verticalPosition: 'top'
            });
          }
        });
    });
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }
}
